// src/components/admin/services/ServiceManagement.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationCenter } from '../../../context/NotificationCenterContext';
import ServiceTable from './ServiceTable';
import ServiceForm from './ServiceForm';
import Modal from '../../common/Modal';
import Loading from '../../common/Loading';
import { serviceService } from '../../../services/service';
import { FaPlus, FaSearch, FaTimes, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';

const ServiceManagement = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    featured: ''
  });

  const isFirstMount = useRef(true);

  // ✅ Only using notification center (no toast)
  const { addNotification: addToCenter } = useNotificationCenter();

  // Service types
  const serviceTypes = [
    { value: 'transport', label: 'Transport' },
    { value: 'tour', label: 'Tour' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'beauty', label: 'Beauty' },
    { value: 'spa', label: 'Spa' },
    { value: 'sport', label: 'Sport' },
    { value: 'other', label: 'Other' }
  ];

  const memoizedFilters = useMemo(() => filters, [
    filters.search,
    filters.type,
    filters.status,
    filters.featured
  ]);

  const handleApiError = useCallback(async (error, context = '') => {
    console.error(`Error ${context}:`, error);
    
    let errorMessage = '';
    if (error.message.includes('404')) {
      errorMessage = `API endpoint not found. Make sure backend server is running at http://localhost:5000`;
    } else if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
      errorMessage = `Cannot connect to server. Make sure backend server is running.`;
    } else {
      errorMessage = `An error occurred: ${error.message}`;
    }
    
    // Show error with SweetAlert ONLY (removed toast)
    await Swal.fire({
      title: 'Error',
      text: errorMessage,
      icon: 'error',
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'OK',
      customClass: {
        popup: 'rounded-2xl',
        title: 'text-xl font-bold',
        confirmButton: 'px-6 py-3 rounded-xl font-semibold'
      }
    });
  }, []);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      
      queryParams.append('page', currentPage);
      queryParams.append('limit', 10);
      
      Object.entries(memoizedFilters).forEach(([key, value]) => {
        if (value && value !== '' && value !== 'undefined') {
          if (key === 'featured') {
            if (value === '1') {
              queryParams.append(key, 'true');
            } else if (value === '0') {
              queryParams.append(key, 'false');
            }
          } else {
            queryParams.append(key, value);
          }
        }
      });

      console.log('Fetching with filters:', Object.fromEntries(queryParams));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/services?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      console.log('API Response:', data);
      
      if (data && data.success && data.data) {
        const servicesData = data.data.services || [];
        setServices(servicesData);
        setTotalPages(data.data.totalPages || 1);
        
        // ✅ ADD: Notification saat berhasil load services pertama kali
        if (isFirstMount.current && servicesData.length > 0) {
          addToCenter({
            type: 'success',
            title: 'Services Loaded',
            message: `Successfully loaded ${servicesData.length} services`
          });
          isFirstMount.current = false;
        }
      } else {
        setServices([]);
        setTotalPages(1);
      }
      
    } catch (error) {
      handleApiError(error, 'fetching services');
      setServices([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, memoizedFilters, handleApiError, addToCenter]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleCreate = useCallback(() => {
    setEditingService(null);
    setShowForm(true);
  }, []);

  const handleEdit = useCallback(async (service) => {
    setLoadingEdit(true);
    try {
      let fullServiceData = service;
      
      if (service.id) {
        try {
          const response = await serviceService.getById(service.id);
          if (response && response.data) {
            fullServiceData = response.data;
          }
          
          // ✅ ADD: Notification saat loading data untuk edit
          addToCenter({
            type: 'info',
            title: 'Editing Service',
            message: `Loading data for "${service.title}"`
          });
        } catch (fetchError) {
          console.error('Error fetching full service data:', fetchError);
          
          // ✅ ADD: Warning notification jika gagal fetch full data
          addToCenter({
            type: 'warning',
            title: 'Limited Data',
            message: 'Using limited data for editing'
          });
        }
      }
      
      setEditingService(fullServiceData);
      setShowForm(true);
      
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: 'Failed to load service data for editing',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
      
      addToCenter({
        type: 'error',
        title: 'Edit Failed',
        message: 'Failed to load service data for editing'
      });
    } finally {
      setLoadingEdit(false);
    }
  }, [addToCenter]);

  const handleDelete = useCallback(async (id, serviceName) => {
    try {
      const response = await serviceService.delete(id);
      if (response && response.success) {
        fetchServices();
      } else {
        throw new Error('Failed to delete service');
      }
    } catch (error) {
      handleApiError(error, 'deleting service');
      throw error;
    }
  }, [fetchServices, handleApiError]);

  const handleToggleFeatured = useCallback(async (id, currentStatus, serviceName) => {
    try {
      const response = await serviceService.toggleFeatured(id);
      if (response && response.success) {
        fetchServices();
      } else {
        throw new Error('Failed to toggle featured status');
      }
    } catch (error) {
      handleApiError(error, 'toggling featured status');
      throw error;
    }
  }, [fetchServices, handleApiError]);

  const handleStatusChange = useCallback(async (id, status, serviceName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/services/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      fetchServices();
    } catch (error) {
      handleApiError(error, 'updating status');
      throw error;
    }
  }, [fetchServices, handleApiError]);

  const handleSave = useCallback(async (formData) => {
    const isEditing = editingService !== null;
    const serviceName = editingService?.title || 'New Service';
    
    try {
      console.log('=== ServiceManagement.handleSave ===');
      console.log('Is Editing:', isEditing);
      console.log('Service ID:', editingService?.id);
      
      // Show loading popup
      Swal.fire({
        title: isEditing ? 'Updating Service...' : 'Creating Service...',
        html: 'Please wait a moment',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      let response;
      
      if (isEditing && editingService.id) {
        // Update existing service
        console.log('Updating service...');
        response = await serviceService.update(editingService.id, formData);
      } else {
        // Create new service
        console.log('Creating new service...');
        response = await serviceService.create(formData);
      }
      
      console.log('Save response:', response);
      
      if (response && response.success) {
        // Close form and refresh data
        setShowForm(false);
        setEditingService(null);
        fetchServices();
        
        // Show success popup
        await Swal.fire({
          title: 'Success!',
          html: isEditing 
            ? `<strong>"${serviceName}"</strong> has been successfully updated` 
            : `New service has been successfully created`,
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'OK',
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'rounded-2xl',
            title: 'text-xl font-bold',
            confirmButton: 'px-6 py-3 rounded-xl font-semibold'
          }
        });
        
        // ✅ ADD: Notification ke notification center (background notification)
        addToCenter({
          type: 'success',
          title: isEditing ? 'Service Updated' : 'Service Created',
          message: isEditing 
            ? `"${serviceName}" has been successfully updated` 
            : 'New service has been successfully created'
        });
      } else {
        throw new Error(response?.error?.message || 'Failed to save service');
      }
    } catch (error) {
      console.error('Save error:', error);
      
      // Close loading popup
      Swal.close();
      
      // Show error popup
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to save service. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
      
      handleApiError(error, isEditing ? 'updating service' : 'creating service');
      throw error;
    }
  }, [editingService, fetchServices, addToCenter, handleApiError]);

  const handleFilterChange = useCallback((key, value) => {
    console.log(`Filter changed: ${key} = ${value}`);
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      search: '',
      type: '',
      status: '',
      featured: ''
    });
    setCurrentPage(1);
    
    // ✅ ADD: Notification saat reset filters
    addToCenter({
      type: 'info',
      title: 'Filters Reset',
      message: 'All filters have been cleared'
    });
  }, [addToCenter]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingService(null);
  }, []);

  const handleViewPublicPage = useCallback(() => {
    navigate('/services');
  }, [navigate]);

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  if (loading && services.length === 0) return <Loading />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-amber-50/30 to-gray-50 p-8">
      <div className="max-w-9xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Service Management</h1>
            <p className="text-gray-600">Manage your services and offerings</p>
          </div>
          <div className="flex gap-3">
            {/* View Public Page Button */}
            <button
              onClick={handleViewPublicPage}
              className="inline-flex items-center px-6 py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
            >
              <FaEye className="mr-2 h-4 w-4" />
              View Public Page
            </button>
            
            {/* Add New Service Button */}
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-6 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
            >
              <FaPlus className="mr-2 h-4 w-4" />
              Add New Service
            </button>
          </div>
        </div>

        {/* Loading Edit Indicator */}
        {loadingEdit && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center">
              <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-sm font-medium text-amber-700">Loading service data for editing...</span>
            </div>
          </div>
        )}

        {/* Compact Filters - All in One Row */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-200/50 p-4">
          <div className="flex flex-wrap gap-2.5">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search services..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all"
                />
              </div>
            </div>

            {/* Type Filter */}
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="px-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all w-auto"
            >
              <option value="">All Types</option>
              {serviceTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all w-auto"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            {/* Featured Filter */}
            <select
              value={filters.featured}
              onChange={(e) => handleFilterChange('featured', e.target.value)}
              className="px-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all w-auto"
            >
              <option value="">Featured</option>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>

            {/* Reset Button */}
            {activeFiltersCount > 0 && (
              <button
                onClick={resetFilters}
                className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all"
              >
                <FaTimes className="mr-1.5 h-3 w-3" />
                Reset
              </button>
            )}
          </div>

          {/* Active Filters Tags */}
          {activeFiltersCount > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs font-medium text-gray-600">{activeFiltersCount} active:</span>
                {filters.search && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    "{filters.search.substring(0, 25)}{filters.search.length > 25 ? '...' : ''}"
                    <button
                      onClick={() => handleFilterChange('search', '')}
                      className="ml-1.5 hover:text-amber-900"
                    >
                      <FaTimes className="h-2.5 w-2.5" />
                    </button>
                  </span>
                )}
                {filters.type && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    {serviceTypes.find(t => t.value === filters.type)?.label}
                    <button
                      onClick={() => handleFilterChange('type', '')}
                      className="ml-1.5 hover:text-amber-900"
                    >
                      <FaTimes className="h-2.5 w-2.5" />
                    </button>
                  </span>
                )}
                {filters.status && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    {filters.status === 'active' ? 'Active' : 'Inactive'}
                    <button
                      onClick={() => handleFilterChange('status', '')}
                      className="ml-1.5 hover:text-amber-900"
                    >
                      <FaTimes className="h-2.5 w-2.5" />
                    </button>
                  </span>
                )}
                {filters.featured && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    Featured: {filters.featured === '1' ? 'Yes' : 'No'}
                    <button
                      onClick={() => handleFilterChange('featured', '')}
                      className="ml-1.5 hover:text-amber-900"
                    >
                      <FaTimes className="h-2.5 w-2.5" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <ServiceTable
          services={services}
          serviceTypes={serviceTypes}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleFeatured={handleToggleFeatured}
          onStatusChange={handleStatusChange}
          loading={loading}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />

        {/* Form Modal */}
        <Modal
          isOpen={showForm}
          onClose={handleCloseForm}
          title={editingService ? `Edit Service: ${editingService.title || 'Loading...'}` : 'Add New Service'}
          size="xl"
        >
          {showForm && (
            <ServiceForm
              service={editingService}
              onSave={handleSave}
              onCancel={handleCloseForm}
              serviceTypes={serviceTypes}
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default ServiceManagement;